import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Newspaper, Mail, Search, FileText, Globe, Brain, Calculator, FileCode, Settings, Key, Shield, Map } from 'lucide-react';
import { trackPageView, trackFeatureUse } from '@/services/analytics';

// Feature Components
import NewsFeeds from './osint/Newsfeed';
import EmailAnalyzer from './osint/EmailAnalyzer';
import IOCAnalyzer from './osint/IOCAnalyzer';
import IOCExtractor from './osint/IOCExtractor';
import DomainMonitoring from './osint/DomainMonitoring';
import AIAssistant from './osint/AIAssistant';
import CVSSCalculator from './osint/CVSSCalculator';
import SigmaRules from './osint/SigmaRules';
import APIKeySettings from './osint/APIKeySettings';
import DDoSReport from './osint/DDoSReport';
import DDoSThreatMap from './osint/DDoSThreatMap';

const OSINTApp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('newsfeed');

  // URL-based tab routing: /osint -> check sub-path
  const pathToTab = {
    'ioc-analyzer': 'ioc',
    'email': 'email',
    'extractor': 'extractor',
    'domain': 'domain',
    'ddos': 'ddos',
    'threatmap': 'threatmap',
    'ai': 'ai',
    'cvss': 'cvss',
    'sigma': 'sigma',
    'settings': 'settings',
    'newsfeed': 'newsfeed',
  };

  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const subPath = pathParts[pathParts.length - 1];
    if (pathToTab[subPath]) {
      setActiveTab(pathToTab[subPath]);
    }
  }, [location.pathname]);

  const features = [
    { id: 'newsfeed', label: 'Newsfeed', icon: Newspaper, component: NewsFeeds },
    { id: 'email', label: 'Email Analyzer', icon: Mail, component: EmailAnalyzer },
    { id: 'ioc', label: 'IOC Analyzer', icon: Search, component: IOCAnalyzer },
    { id: 'extractor', label: 'IOC Extractor', icon: FileText, component: IOCExtractor },
    { id: 'domain', label: 'Domain Monitoring', icon: Globe, component: DomainMonitoring },
    { id: 'ddos', label: 'DDoS Report', icon: Shield, component: DDoSReport },
    { id: 'threatmap', label: 'Threat Intel Map', icon: Map, component: DDoSThreatMap },
    { id: 'ai', label: 'AI Assistant', icon: Brain, component: AIAssistant },
    { id: 'cvss', label: 'CVSS Calculator', icon: Calculator, component: CVSSCalculator },
    { id: 'sigma', label: 'Sigma Rules', icon: FileCode, component: SigmaRules },
    { id: 'settings', label: 'API Settings', icon: Key, component: APIKeySettings },
  ];

  useEffect(() => {
    trackPageView('Beskar OSINT', location.pathname);
  }, [location]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const feature = features.find(f => f.id === tab);
    if (feature) {
      trackFeatureUse('Beskar OSINT', feature.label);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-muted">
      {/* Header */}
      <div className="bg-white border-b px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/apps')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            All Apps
          </Button>
          <div className="h-6 w-px bg-border"></div>
          <img src="/logo-beskar.png" alt="Beskar OSINT" className="h-8" />
          <h1 className="font-semibold text-lg beskar-text-primary">Beskar OSINT</h1>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setActiveTab('settings')}
        >
          <Settings className="w-4 h-4 mr-2" />
          API Keys
        </Button>
      </div>

      {/* Feature Tabs */}
      <div className="flex-1 overflow-hidden">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="h-full flex flex-col">
          <div className="bg-white border-b px-6 overflow-x-auto">
            <TabsList className="h-12">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <TabsTrigger
                    key={feature.id}
                    value={feature.id}
                    className="flex items-center gap-2"
                  >
                    <Icon className="w-4 h-4" />
                    {feature.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          <div className="flex-1 overflow-auto">
            {features.map((feature) => {
              const Component = feature.component;
              return (
                <TabsContent key={feature.id} value={feature.id} className="h-full m-0">
                  <Component />
                </TabsContent>
              );
            })}
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default OSINTApp;